import React, { Suspense, lazy, Component } from 'react';
import { BrowserRouter, Routes, Route } from 'react-router-dom';
import { AppLayout } from '../layouts/AppLayout';
import { PageTransitionWrapper } from '../../shared/ui/PageTransitionWrapper';
import { ProtectedRoute } from './ProtectedRoute';
import { LoadingScreen } from '../../shared/ui/LoadingScreen';
import { Login } from '../../features/auth/pages/Login';
// Lazy-loaded feature pages. Named exports are mapped to default for lazy.
const lazyNamed = <T extends Record<string, unknown>,>(loader: () => Promise<T>, name: keyof T) => lazy(() => loader().then(m => ({
  default: m[name] as ComponentType<unknown>
})));
// analytics
const Overview = lazyNamed(() => import('../../features/analytics'), 'Overview');
const CustomMetrics = lazyNamed(() => import('../../features/analytics'), 'CustomMetrics');
const Nadi = lazyNamed(() => import('../../features/analytics'), 'Nadi');
const Reports = lazyNamed(() => import('../../features/analytics'), 'Reports');
const CardMonitoringPrograms = lazyNamed(() => import('../../features/analytics'), 'CardMonitoringPrograms');
const VampDetail = lazyNamed(() => import('../../features/analytics'), 'VampDetail');
const PaymentAnalytics = lazyNamed(() => import('../../features/analytics'), 'PaymentAnalytics');
// notifications
const Notifications = lazyNamed(() => import('../../features/notifications'), 'Notifications');
// payments
const PaymentRequests = lazyNamed(() => import('../../features/payments'), 'PaymentRequests');
const BulkPayments = lazyNamed(() => import('../../features/payments'), 'BulkPayments');
const BulkPaymentDetail = lazyNamed(() => import('../../features/payments'), 'BulkPaymentDetail');
const VirtualTerminal = lazyNamed(() => import('../../features/payments'), 'VirtualTerminal');
const QRCodes = lazyNamed(() => import('../../features/payments'), 'QRCodes');
// settings
const Settings = lazyNamed(() => import('../../features/settings'), 'Settings');
const UpdatePayoutAccount = lazyNamed(() => import('../../features/settings'), 'UpdatePayoutAccount');
// balance
const PayoutSettings = lazyNamed(() => import('../../features/balance'), 'PayoutSettings');
const BalanceSummary = lazyNamed(() => import('../../features/balance'), 'BalanceSummary');
const BalanceActivity = lazyNamed(() => import('../../features/balance'), 'BalanceActivity');
const BalanceActivityDetail = lazyNamed(() => import('../../features/balance'), 'BalanceActivityDetail');
const Payouts = lazyNamed(() => import('../../features/balance'), 'Payouts');
const PayoutDetail = lazyNamed(() => import('../../features/balance'), 'PayoutDetail');
// terminals
const PhysicalTerminals = lazyNamed(() => import('../../features/terminals'), 'PhysicalTerminals');
const SoftPosTerminals = lazyNamed(() => import('../../features/terminals'), 'SoftPosTerminals');
const Connect = lazyNamed(() => import('../../features/terminals'), 'Connect');
// developers
const WebhookEndpoints = lazyNamed(() => import('../../features/developers'), 'WebhookEndpoints');
const WebhookLogs = lazyNamed(() => import('../../features/developers'), 'WebhookLogs');
// tutorials
const Tutorials = lazyNamed(() => import('../../features/tutorials'), 'Tutorials');
const TutorialDetail = lazyNamed(() => import('../../features/tutorials'), 'TutorialDetail');
// shops (orders, shops, campaigns, sales, inventory)
const Orders = lazyNamed(() => import('../../features/shops'), 'Orders');
const OrderDetail = lazyNamed(() => import('../../features/shops'), 'OrderDetail');
const Shops = lazyNamed(() => import('../../features/shops'), 'Shops');
const ShopDetails = lazyNamed(() => import('../../features/shops'), 'ShopDetails');
const Campaigns = lazyNamed(() => import('../../features/shops'), 'Campaigns');
const CampaignDetails = lazyNamed(() => import('../../features/shops'), 'CampaignDetails');
const CampaignForm = lazyNamed(() => import('../../features/shops'), 'CampaignForm');
const Inventory = lazyNamed(() => import('../../features/shops'), 'Inventory');
const Sales = lazyNamed(() => import('../../features/shops'), 'Sales');
const SaleDetail = lazyNamed(() => import('../../features/shops'), 'SaleDetail');
const ShopSettings = lazyNamed(() => import('../../features/shops'), 'ShopSettings');
const ProductDetail = lazyNamed(() => import('../../features/shops'), 'ProductDetail');
const ProductEdit = lazyNamed(() => import('../../features/shops'), 'ProductEdit');
// rooms
const PropertyInformation = lazyNamed(() => import('../../features/rooms'), 'PropertyInformation');
const Rooms = lazyNamed(() => import('../../features/rooms'), 'Rooms');
const AddRoom = lazyNamed(() => import('../../features/rooms'), 'AddRoom');
const EditRoom = lazyNamed(() => import('../../features/rooms'), 'EditRoom');
const RoomDetail = lazyNamed(() => import('../../features/rooms'), 'RoomDetail');
const Rates = lazyNamed(() => import('../../features/rooms'), 'Rates');
const Reservations = lazyNamed(() => import('../../features/rooms'), 'Reservations');
const ReservationDetail = lazyNamed(() => import('../../features/rooms'), 'ReservationDetail');
// invoices
const Invoices = lazyNamed(() => import('../../features/invoices'), 'Invoices');
const InvoiceDetail = lazyNamed(() => import('../../features/invoices'), 'InvoiceDetail');
const InvoiceForm = lazyNamed(() => import('../../features/invoices'), 'InvoiceForm');
// customers
const Customers = lazyNamed(() => import('../../features/customers'), 'Customers');
const CustomerDetail = lazyNamed(() => import('../../features/customers'), 'CustomerDetail');
const CustomerForm = lazyNamed(() => import('../../features/customers'), 'CustomerForm');
// suppliers
const Suppliers = lazyNamed(() => import('../../features/suppliers'), 'Suppliers');
const SupplierDetail = lazyNamed(() => import('../../features/suppliers'), 'SupplierDetail');
const SupplierForm = lazyNamed(() => import('../../features/suppliers'), 'SupplierForm');
const SupplierSelfService = lazyNamed(() => import('../../features/suppliers'), 'SupplierSelfService');
const BulkSupplierUpload = lazyNamed(() => import('../../features/suppliers'), 'BulkSupplierUpload');
// bills
const Bills = lazyNamed(() => import('../../features/bills'), 'Bills');
const BillDetail = lazyNamed(() => import('../../features/bills'), 'BillDetail');
const BillForm = lazyNamed(() => import('../../features/bills'), 'BillForm');
const BillApprovalWizard = lazyNamed(() => import('../../features/bills'), 'BillApprovalWizard');
const BillFinancingWizard = lazyNamed(() => import('../../features/bills'), 'BillFinancingWizard');
const BulkBillUpload = lazyNamed(() => import('../../features/bills'), 'BulkBillUpload');
const Financing = lazyNamed(() => import('../../features/bills'), 'Financing');
const FacilityDetail = lazyNamed(() => import('../../features/bills'), 'FacilityDetail');
const FinancingApplyWizard = lazyNamed(() => import('../../features/bills'), 'FinancingApplyWizard');
// disputes
const Disputes = lazyNamed(() => import('../../features/disputes'), 'Disputes');
const DisputeDetail = lazyNamed(() => import('../../features/disputes'), 'DisputeDetail');
const ChallengeDispute = lazyNamed(() => import('../../features/disputes'), 'ChallengeDispute');
// refunds
const Refunds = lazyNamed(() => import('../../features/refunds'), 'Refunds');
const RefundDetail = lazyNamed(() => import('../../features/refunds'), 'RefundDetail');
// system
const ActivityLogs = lazyNamed(() => import('../../features/system'), 'ActivityLogs');
const Requests = lazyNamed(() => import('../../features/system'), 'Requests');
const RequestDetail = lazyNamed(() => import('../../features/system'), 'RequestDetail');
export function AppRouter() {
  return <BrowserRouter data-id="element-235">
      <Suspense fallback={<LoadingScreen data-id="element-237" />} data-id="element-236">
        <Routes data-id="element-238">
          <Route path="/" element={<Login data-id="element-240" />} data-id="element-239" />
          <Route path="/supplier-onboarding/:id" element={<SupplierSelfService data-id="element-242" />} data-id="element-241" />

          <Route path="/overview" element={<ProtectedRoute data-id="element-244">
                <AppLayout data-id="element-245">
                  <PageTransitionWrapper data-id="element-246">
                    <Overview data-id="element-247" />
                  </PageTransitionWrapper>
                </AppLayout>
              </ProtectedRoute>} data-id="element-243" />
          <Route path="/notifications" element={<ProtectedRoute data-id="element-249">
                <AppLayout data-id="element-250">
                  <PageTransitionWrapper data-id="element-251">
                    <Notifications data-id="element-252" />
                  </PageTransitionWrapper>
                </AppLayout>
              </ProtectedRoute>} data-id="element-248" />
          <Route path="/payment-requests" element={<ProtectedRoute data-id="element-254">
                <AppLayout data-id="element-255">
                  <PageTransitionWrapper data-id="element-256">
                    <PaymentRequests data-id="element-257" />
                  </PageTransitionWrapper>
                </AppLayout>
              </ProtectedRoute>} data-id="element-253" />
          <Route path="/qr-codes" element={<ProtectedRoute data-id="element-259">
                <AppLayout data-id="element-260">
                  <PageTransitionWrapper data-id="element-261">
                    <QRCodes data-id="element-262" />
                  </PageTransitionWrapper>
                </AppLayout>
              </ProtectedRoute>} data-id="element-258" />
          <Route path="/bulk-payments" element={<ProtectedRoute data-id="element-264">
                <AppLayout data-id="element-265">
                  <PageTransitionWrapper data-id="element-266">
                    <BulkPayments data-id="element-267" />
                  </PageTransitionWrapper>
                </AppLayout>
              </ProtectedRoute>} data-id="element-263" />
          <Route path="/bulk-payment-detail/:id" element={<ProtectedRoute data-id="element-269">
                <AppLayout data-id="element-270">
                  <PageTransitionWrapper data-id="element-271">
                    <BulkPaymentDetail data-id="element-272" />
                  </PageTransitionWrapper>
                </AppLayout>
              </ProtectedRoute>} data-id="element-268" />
          <Route path="/settings" element={<ProtectedRoute data-id="element-274">
                <AppLayout data-id="element-275">
                  <PageTransitionWrapper data-id="element-276">
                    <Settings data-id="element-277" />
                  </PageTransitionWrapper>
                </AppLayout>
              </ProtectedRoute>} data-id="element-273" />
          <Route path="/settings/payouts" element={<ProtectedRoute data-id="element-279">
                <AppLayout data-id="element-280">
                  <PageTransitionWrapper data-id="element-281">
                    <PayoutSettings data-id="element-282" />
                  </PageTransitionWrapper>
                </AppLayout>
              </ProtectedRoute>} data-id="element-278" />
          <Route path="/settings/payout-account/edit" element={<ProtectedRoute data-id="element-284">
                <AppLayout data-id="element-285">
                  <PageTransitionWrapper data-id="element-286">
                    <UpdatePayoutAccount data-id="element-287" />
                  </PageTransitionWrapper>
                </AppLayout>
              </ProtectedRoute>} data-id="element-283" />
          <Route path="/virtual-terminal" element={<ProtectedRoute data-id="element-289">
                <AppLayout data-id="element-290">
                  <PageTransitionWrapper data-id="element-291">
                    <VirtualTerminal data-id="element-292" />
                  </PageTransitionWrapper>
                </AppLayout>
              </ProtectedRoute>} data-id="element-288" />
          <Route path="/physical-terminals" element={<ProtectedRoute data-id="element-294">
                <AppLayout data-id="element-295">
                  <PageTransitionWrapper data-id="element-296">
                    <PhysicalTerminals data-id="element-297" />
                  </PageTransitionWrapper>
                </AppLayout>
              </ProtectedRoute>} data-id="element-293" />
          <Route path="/softpos-terminals" element={<ProtectedRoute data-id="element-299">
                <AppLayout data-id="element-300">
                  <PageTransitionWrapper data-id="element-301">
                    <SoftPosTerminals data-id="element-302" />
                  </PageTransitionWrapper>
                </AppLayout>
              </ProtectedRoute>} data-id="element-298" />
          <Route path="/connect" element={<ProtectedRoute data-id="element-304">
                <AppLayout data-id="element-305">
                  <PageTransitionWrapper data-id="element-306">
                    <Connect data-id="element-307" />
                  </PageTransitionWrapper>
                </AppLayout>
              </ProtectedRoute>} data-id="element-303" />
          <Route path="/webhooks/endpoints" element={<ProtectedRoute data-id="element-309">
                <AppLayout data-id="element-310">
                  <PageTransitionWrapper data-id="element-311">
                    <WebhookEndpoints data-id="element-312" />
                  </PageTransitionWrapper>
                </AppLayout>
              </ProtectedRoute>} data-id="element-308" />
          <Route path="/webhooks/logs" element={<ProtectedRoute data-id="element-314">
                <AppLayout data-id="element-315">
                  <PageTransitionWrapper data-id="element-316">
                    <WebhookLogs data-id="element-317" />
                  </PageTransitionWrapper>
                </AppLayout>
              </ProtectedRoute>} data-id="element-313" />
          <Route path="/tutorials" element={<ProtectedRoute data-id="element-319">
                <AppLayout data-id="element-320">
                  <PageTransitionWrapper data-id="element-321">
                    <Tutorials data-id="element-322" />
                  </PageTransitionWrapper>
                </AppLayout>
              </ProtectedRoute>} data-id="element-318" />
          <Route path="/tutorial/:id" element={<ProtectedRoute data-id="element-324">
                <AppLayout data-id="element-325">
                  <PageTransitionWrapper data-id="element-326">
                    <TutorialDetail data-id="element-327" />
                  </PageTransitionWrapper>
                </AppLayout>
              </ProtectedRoute>} data-id="element-323" />
          <Route path="/orders" element={<ProtectedRoute data-id="element-329">
                <AppLayout data-id="element-330">
                  <PageTransitionWrapper data-id="element-331">
                    <Orders data-id="element-332" />
                  </PageTransitionWrapper>
                </AppLayout>
              </ProtectedRoute>} data-id="element-328" />
          <Route path="/order-detail/:id" element={<ProtectedRoute data-id="element-334">
                <AppLayout data-id="element-335">
                  <PageTransitionWrapper data-id="element-336">
                    <OrderDetail data-id="element-337" />
                  </PageTransitionWrapper>
                </AppLayout>
              </ProtectedRoute>} data-id="element-333" />
          <Route path="/shops" element={<ProtectedRoute data-id="element-339">
                <AppLayout data-id="element-340">
                  <PageTransitionWrapper data-id="element-341">
                    <Shops data-id="element-342" />
                  </PageTransitionWrapper>
                </AppLayout>
              </ProtectedRoute>} data-id="element-338" />
          <Route path="/shop-details" element={<ProtectedRoute data-id="element-344">
                <AppLayout data-id="element-345">
                  <PageTransitionWrapper data-id="element-346">
                    <ShopDetails data-id="element-347" />
                  </PageTransitionWrapper>
                </AppLayout>
              </ProtectedRoute>} data-id="element-343" />
          <Route path="/shop-settings/:shopId" element={<ProtectedRoute data-id="element-349">
                <AppLayout data-id="element-350">
                  <PageTransitionWrapper data-id="element-351">
                    <ShopSettings data-id="element-352" />
                  </PageTransitionWrapper>
                </AppLayout>
              </ProtectedRoute>} data-id="element-348" />
          <Route path="/shop-settings" element={<ProtectedRoute data-id="element-354">
                <AppLayout data-id="element-355">
                  <PageTransitionWrapper data-id="element-356">
                    <ShopSettings data-id="element-357" />
                  </PageTransitionWrapper>
                </AppLayout>
              </ProtectedRoute>} data-id="element-353" />
          <Route path="/product-detail/:productId" element={<ProtectedRoute data-id="element-359">
                <AppLayout data-id="element-360">
                  <PageTransitionWrapper data-id="element-361">
                    <ProductDetail data-id="element-362" />
                  </PageTransitionWrapper>
                </AppLayout>
              </ProtectedRoute>} data-id="element-358" />
          <Route path="/product-edit/:productId" element={<ProtectedRoute data-id="element-364">
                <AppLayout data-id="element-365">
                  <PageTransitionWrapper data-id="element-366">
                    <ProductEdit data-id="element-367" />
                  </PageTransitionWrapper>
                </AppLayout>
              </ProtectedRoute>} data-id="element-363" />
          <Route path="/campaigns" element={<ProtectedRoute data-id="element-369">
                <AppLayout data-id="element-370">
                  <PageTransitionWrapper data-id="element-371">
                    <Campaigns data-id="element-372" />
                  </PageTransitionWrapper>
                </AppLayout>
              </ProtectedRoute>} data-id="element-368" />
          <Route path="/campaign-details" element={<ProtectedRoute data-id="element-374">
                <AppLayout data-id="element-375">
                  <PageTransitionWrapper data-id="element-376">
                    <CampaignDetails data-id="element-377" />
                  </PageTransitionWrapper>
                </AppLayout>
              </ProtectedRoute>} data-id="element-373" />
          <Route path="/campaign-form" element={<ProtectedRoute data-id="element-379">
                <AppLayout data-id="element-380">
                  <PageTransitionWrapper data-id="element-381">
                    <CampaignForm data-id="element-382" />
                  </PageTransitionWrapper>
                </AppLayout>
              </ProtectedRoute>} data-id="element-378" />
          <Route path="/property-information" element={<ProtectedRoute data-id="element-384">
                <AppLayout data-id="element-385">
                  <PageTransitionWrapper data-id="element-386">
                    <PropertyInformation data-id="element-387" />
                  </PageTransitionWrapper>
                </AppLayout>
              </ProtectedRoute>} data-id="element-383" />
          <Route path="/rooms" element={<ProtectedRoute data-id="element-389">
                <AppLayout data-id="element-390">
                  <PageTransitionWrapper data-id="element-391">
                    <Rooms data-id="element-392" />
                  </PageTransitionWrapper>
                </AppLayout>
              </ProtectedRoute>} data-id="element-388" />
          <Route path="/add-room" element={<ProtectedRoute data-id="element-394">
                <AppLayout data-id="element-395">
                  <PageTransitionWrapper data-id="element-396">
                    <AddRoom data-id="element-397" />
                  </PageTransitionWrapper>
                </AppLayout>
              </ProtectedRoute>} data-id="element-393" />
          <Route path="/edit-room" element={<ProtectedRoute data-id="element-399">
                <AppLayout data-id="element-400">
                  <PageTransitionWrapper data-id="element-401">
                    <EditRoom data-id="element-402" />
                  </PageTransitionWrapper>
                </AppLayout>
              </ProtectedRoute>} data-id="element-398" />
          <Route path="/room-detail" element={<ProtectedRoute data-id="element-404">
                <AppLayout data-id="element-405">
                  <PageTransitionWrapper data-id="element-406">
                    <RoomDetail data-id="element-407" />
                  </PageTransitionWrapper>
                </AppLayout>
              </ProtectedRoute>} data-id="element-403" />
          <Route path="/inventory" element={<ProtectedRoute data-id="element-409">
                <AppLayout data-id="element-410">
                  <PageTransitionWrapper data-id="element-411">
                    <Inventory data-id="element-412" />
                  </PageTransitionWrapper>
                </AppLayout>
              </ProtectedRoute>} data-id="element-408" />
          <Route path="/rates" element={<ProtectedRoute data-id="element-414">
                <AppLayout data-id="element-415">
                  <PageTransitionWrapper data-id="element-416">
                    <Rates data-id="element-417" />
                  </PageTransitionWrapper>
                </AppLayout>
              </ProtectedRoute>} data-id="element-413" />
          <Route path="/reservations" element={<ProtectedRoute data-id="element-419">
                <AppLayout data-id="element-420">
                  <PageTransitionWrapper data-id="element-421">
                    <Reservations data-id="element-422" />
                  </PageTransitionWrapper>
                </AppLayout>
              </ProtectedRoute>} data-id="element-418" />
          <Route path="/reservation-detail" element={<ProtectedRoute data-id="element-424">
                <AppLayout data-id="element-425">
                  <PageTransitionWrapper data-id="element-426">
                    <ReservationDetail data-id="element-427" />
                  </PageTransitionWrapper>
                </AppLayout>
              </ProtectedRoute>} data-id="element-423" />
          <Route path="/invoices" element={<ProtectedRoute data-id="element-429">
                <AppLayout data-id="element-430">
                  <PageTransitionWrapper data-id="element-431">
                    <Invoices data-id="element-432" />
                  </PageTransitionWrapper>
                </AppLayout>
              </ProtectedRoute>} data-id="element-428" />
          <Route path="/invoice-detail/:id" element={<ProtectedRoute data-id="element-434">
                <AppLayout data-id="element-435">
                  <PageTransitionWrapper data-id="element-436">
                    <InvoiceDetail data-id="element-437" />
                  </PageTransitionWrapper>
                </AppLayout>
              </ProtectedRoute>} data-id="element-433" />
          <Route path="/invoice-form" element={<ProtectedRoute data-id="element-439">
                <AppLayout data-id="element-440">
                  <PageTransitionWrapper data-id="element-441">
                    <InvoiceForm data-id="element-442" />
                  </PageTransitionWrapper>
                </AppLayout>
              </ProtectedRoute>} data-id="element-438" />
          <Route path="/customers" element={<ProtectedRoute data-id="element-444">
                <AppLayout data-id="element-445">
                  <PageTransitionWrapper data-id="element-446">
                    <Customers data-id="element-447" />
                  </PageTransitionWrapper>
                </AppLayout>
              </ProtectedRoute>} data-id="element-443" />
          <Route path="/customer-detail/:id" element={<ProtectedRoute data-id="element-449">
                <AppLayout data-id="element-450">
                  <PageTransitionWrapper data-id="element-451">
                    <CustomerDetail data-id="element-452" />
                  </PageTransitionWrapper>
                </AppLayout>
              </ProtectedRoute>} data-id="element-448" />
          <Route path="/customer-form" element={<ProtectedRoute data-id="element-454">
                <AppLayout data-id="element-455">
                  <PageTransitionWrapper data-id="element-456">
                    <CustomerForm data-id="element-457" />
                  </PageTransitionWrapper>
                </AppLayout>
              </ProtectedRoute>} data-id="element-453" />
          <Route path="/sales" element={<ProtectedRoute data-id="element-459">
                <AppLayout data-id="element-460">
                  <PageTransitionWrapper data-id="element-461">
                    <Sales data-id="element-462" />
                  </PageTransitionWrapper>
                </AppLayout>
              </ProtectedRoute>} data-id="element-458" />
          <Route path="/sale-detail/:id" element={<ProtectedRoute data-id="element-464">
                <AppLayout data-id="element-465">
                  <PageTransitionWrapper data-id="element-466">
                    <SaleDetail data-id="element-467" />
                  </PageTransitionWrapper>
                </AppLayout>
              </ProtectedRoute>} data-id="element-463" />
          <Route path="/balance" element={<ProtectedRoute data-id="element-469">
                <AppLayout data-id="element-470">
                  <PageTransitionWrapper data-id="element-471">
                    <BalanceSummary data-id="element-472" />
                  </PageTransitionWrapper>
                </AppLayout>
              </ProtectedRoute>} data-id="element-468" />
          <Route path="/balance-activity" element={<ProtectedRoute data-id="element-474">
                <AppLayout data-id="element-475">
                  <PageTransitionWrapper data-id="element-476">
                    <BalanceActivity data-id="element-477" />
                  </PageTransitionWrapper>
                </AppLayout>
              </ProtectedRoute>} data-id="element-473" />
          <Route path="/balance-activity/:id" element={<ProtectedRoute data-id="element-479">
                <AppLayout data-id="element-480">
                  <PageTransitionWrapper data-id="element-481">
                    <BalanceActivityDetail data-id="element-482" />
                  </PageTransitionWrapper>
                </AppLayout>
              </ProtectedRoute>} data-id="element-478" />
          <Route path="/payouts" element={<ProtectedRoute data-id="element-484">
                <AppLayout data-id="element-485">
                  <PageTransitionWrapper data-id="element-486">
                    <Payouts data-id="element-487" />
                  </PageTransitionWrapper>
                </AppLayout>
              </ProtectedRoute>} data-id="element-483" />
          <Route path="/payouts/:id" element={<ProtectedRoute data-id="element-489">
                <AppLayout data-id="element-490">
                  <PageTransitionWrapper data-id="element-491">
                    <PayoutDetail data-id="element-492" />
                  </PageTransitionWrapper>
                </AppLayout>
              </ProtectedRoute>} data-id="element-488" />
          <Route path="/suppliers" element={<ProtectedRoute data-id="element-494">
                <AppLayout data-id="element-495">
                  <PageTransitionWrapper data-id="element-496">
                    <Suppliers data-id="element-497" />
                  </PageTransitionWrapper>
                </AppLayout>
              </ProtectedRoute>} data-id="element-493" />
          <Route path="/supplier-detail/:id" element={<ProtectedRoute data-id="element-499">
                <AppLayout data-id="element-500">
                  <PageTransitionWrapper data-id="element-501">
                    <SupplierDetail data-id="element-502" />
                  </PageTransitionWrapper>
                </AppLayout>
              </ProtectedRoute>} data-id="element-498" />
          <Route path="/supplier-form" element={<ProtectedRoute data-id="element-504">
                <AppLayout data-id="element-505">
                  <PageTransitionWrapper data-id="element-506">
                    <SupplierForm data-id="element-507" />
                  </PageTransitionWrapper>
                </AppLayout>
              </ProtectedRoute>} data-id="element-503" />
          <Route path="/supplier-bulk-upload" element={<ProtectedRoute data-id="element-509">
                <AppLayout data-id="element-510">
                  <PageTransitionWrapper data-id="element-511">
                    <BulkSupplierUpload data-id="element-512" />
                  </PageTransitionWrapper>
                </AppLayout>
              </ProtectedRoute>} data-id="element-508" />
          <Route path="/bill-bulk-upload" element={<ProtectedRoute data-id="element-514">
                <AppLayout data-id="element-515">
                  <PageTransitionWrapper data-id="element-516">
                    <BulkBillUpload data-id="element-517" />
                  </PageTransitionWrapper>
                </AppLayout>
              </ProtectedRoute>} data-id="element-513" />
          <Route path="/bills" element={<ProtectedRoute data-id="element-519">
                <AppLayout data-id="element-520">
                  <PageTransitionWrapper data-id="element-521">
                    <Bills data-id="element-522" />
                  </PageTransitionWrapper>
                </AppLayout>
              </ProtectedRoute>} data-id="element-518" />
          <Route path="/bills/financing" element={<ProtectedRoute data-id="element-524">
                <AppLayout data-id="element-525">
                  <PageTransitionWrapper data-id="element-526">
                    <Financing data-id="element-527" />
                  </PageTransitionWrapper>
                </AppLayout>
              </ProtectedRoute>} data-id="element-523" />
          <Route path="/bills/financing/apply" element={<ProtectedRoute data-id="element-529">
                <FinancingApplyWizard data-id="element-530" />
              </ProtectedRoute>} data-id="element-528" />
          <Route path="/bills/financing/facility/:id" element={<ProtectedRoute data-id="element-532">
                <AppLayout data-id="element-533">
                  <PageTransitionWrapper data-id="element-534">
                    <FacilityDetail data-id="element-535" />
                  </PageTransitionWrapper>
                </AppLayout>
              </ProtectedRoute>} data-id="element-531" />
          <Route path="/bill-detail/:id" element={<ProtectedRoute data-id="element-537">
                <AppLayout data-id="element-538">
                  <PageTransitionWrapper data-id="element-539">
                    <BillDetail data-id="element-540" />
                  </PageTransitionWrapper>
                </AppLayout>
              </ProtectedRoute>} data-id="element-536" />
          <Route path="/bill-form" element={<ProtectedRoute data-id="element-542">
                <AppLayout data-id="element-543">
                  <PageTransitionWrapper data-id="element-544">
                    <BillForm data-id="element-545" />
                  </PageTransitionWrapper>
                </AppLayout>
              </ProtectedRoute>} data-id="element-541" />
          <Route path="/bill-approval/:id" element={<ProtectedRoute data-id="element-547">
                <AppLayout data-id="element-548">
                  <PageTransitionWrapper data-id="element-549">
                    <BillApprovalWizard data-id="element-550" />
                  </PageTransitionWrapper>
                </AppLayout>
              </ProtectedRoute>} data-id="element-546" />
          <Route path="/bill-financing/:id" element={<ProtectedRoute data-id="element-552">
                <AppLayout data-id="element-553">
                  <PageTransitionWrapper data-id="element-554">
                    <BillFinancingWizard data-id="element-555" />
                  </PageTransitionWrapper>
                </AppLayout>
              </ProtectedRoute>} data-id="element-551" />
          <Route path="/disputes" element={<ProtectedRoute data-id="element-557">
                <AppLayout data-id="element-558">
                  <PageTransitionWrapper data-id="element-559">
                    <Disputes data-id="element-560" />
                  </PageTransitionWrapper>
                </AppLayout>
              </ProtectedRoute>} data-id="element-556" />
          <Route path="/dispute-detail/:id" element={<ProtectedRoute data-id="element-562">
                <AppLayout data-id="element-563">
                  <PageTransitionWrapper data-id="element-564">
                    <DisputeDetail data-id="element-565" />
                  </PageTransitionWrapper>
                </AppLayout>
              </ProtectedRoute>} data-id="element-561" />
          <Route path="/disputes/:id" element={<ProtectedRoute data-id="element-567">
                <AppLayout data-id="element-568">
                  <PageTransitionWrapper data-id="element-569">
                    <DisputeDetail data-id="element-570" />
                  </PageTransitionWrapper>
                </AppLayout>
              </ProtectedRoute>} data-id="element-566" />
          <Route path="/disputes/:id/challenge" element={<ProtectedRoute data-id="element-572">
                <ChallengeDispute data-id="element-573" />
              </ProtectedRoute>} data-id="element-571" />
          <Route path="/refunds" element={<ProtectedRoute data-id="element-575">
                <AppLayout data-id="element-576">
                  <PageTransitionWrapper data-id="element-577">
                    <Refunds data-id="element-578" />
                  </PageTransitionWrapper>
                </AppLayout>
              </ProtectedRoute>} data-id="element-574" />
          <Route path="/refund-detail/:id" element={<ProtectedRoute data-id="element-580">
                <AppLayout data-id="element-581">
                  <PageTransitionWrapper data-id="element-582">
                    <RefundDetail data-id="element-583" />
                  </PageTransitionWrapper>
                </AppLayout>
              </ProtectedRoute>} data-id="element-579" />
          <Route path="/custom-metrics" element={<ProtectedRoute data-id="element-585">
                <AppLayout data-id="element-586">
                  <PageTransitionWrapper data-id="element-587">
                    <CustomMetrics data-id="element-588" />
                  </PageTransitionWrapper>
                </AppLayout>
              </ProtectedRoute>} data-id="element-584" />
          <Route path="/nadi" element={<ProtectedRoute data-id="element-590">
                <AppLayout data-id="element-591">
                  <PageTransitionWrapper data-id="element-592">
                    <Nadi data-id="element-593" />
                  </PageTransitionWrapper>
                </AppLayout>
              </ProtectedRoute>} data-id="element-589" />
          <Route path="/reports" element={<ProtectedRoute data-id="element-595">
                <AppLayout data-id="element-596">
                  <PageTransitionWrapper data-id="element-597">
                    <Reports data-id="element-598" />
                  </PageTransitionWrapper>
                </AppLayout>
              </ProtectedRoute>} data-id="element-594" />
          <Route path="/card-monitoring" element={<ProtectedRoute data-id="element-600">
                <AppLayout data-id="element-601">
                  <PageTransitionWrapper data-id="element-602">
                    <CardMonitoringPrograms data-id="element-603" />
                  </PageTransitionWrapper>
                </AppLayout>
              </ProtectedRoute>} data-id="element-599" />
          <Route path="/card-monitoring/vamp" element={<ProtectedRoute data-id="element-605">
                <AppLayout data-id="element-606">
                  <PageTransitionWrapper data-id="element-607">
                    <VampDetail data-id="element-608" />
                  </PageTransitionWrapper>
                </AppLayout>
              </ProtectedRoute>} data-id="element-604" />
          <Route path="/payment-analytics" element={<ProtectedRoute data-id="element-610">
                <AppLayout data-id="element-611">
                  <PageTransitionWrapper data-id="element-612">
                    <PaymentAnalytics data-id="element-613" />
                  </PageTransitionWrapper>
                </AppLayout>
              </ProtectedRoute>} data-id="element-609" />
          <Route path="/activity-logs" element={<ProtectedRoute data-id="element-615">
                <AppLayout data-id="element-616">
                  <PageTransitionWrapper data-id="element-617">
                    <ActivityLogs data-id="element-618" />
                  </PageTransitionWrapper>
                </AppLayout>
              </ProtectedRoute>} data-id="element-614" />
          <Route path="/requests" element={<ProtectedRoute data-id="element-620">
                <AppLayout data-id="element-621">
                  <PageTransitionWrapper data-id="element-622">
                    <Requests data-id="element-623" />
                  </PageTransitionWrapper>
                </AppLayout>
              </ProtectedRoute>} data-id="element-619" />
          <Route path="/requests/:id" element={<ProtectedRoute data-id="element-625">
                <AppLayout data-id="element-626">
                  <PageTransitionWrapper data-id="element-627">
                    <RequestDetail data-id="element-628" />
                  </PageTransitionWrapper>
                </AppLayout>
              </ProtectedRoute>} data-id="element-624" />
        </Routes>
      </Suspense>
    </BrowserRouter>;
}